import { css } from "@emotion/react";

import {
  bodySm,
  bulletsSm,
  fontSizeLineHeight,
  h3,
  h3Sm,
  h4,
  h4Sm,
  h5,
  h5Sm,
  h6,
  mediaQueries,
  numbersSm,
  pxToRem,
  rgbaFromHex,
  weights,
  tableSm,
  transition,
  respSpacing,
} from "@upsun/react-component-library/src/styles";

import { colors } from "./colors";

import {
  PrimaryTxtInvDefault,
  PrimaryBgDefault,
} from "@upsun/react-component-library/tokens/js/light"

export const center = css`
  text-align: center;
`;

// For blog
export const calloutMutedStyles = css`
  padding: ${pxToRem(40)};
  margin-bottom: ${pxToRem(32)};
  border-radius: ${pxToRem(32)};
  background: ${colors.neutral900};

  > *:last-child {
    margin-bottom: 0;
  }
`;

export const calloutStrongStyles = css`
  position: relative;
  margin: ${pxToRem(56)} 0;
  padding: ${pxToRem(48)};
  border-radius: ${pxToRem(16)};
  border-top-right-radius: 0;
  background-image:
    linear-gradient(90deg, ${PrimaryBgDefault} 100%, transparent 100%),
    linear-gradient(270deg, ${PrimaryBgDefault} 100%, transparent 100%);
  background-size: calc(100% - ${pxToRem(48)}) 100%, 100% calc(100% - ${pxToRem(48)});
  background-position: bottom left;
  background-repeat: no-repeat;
  color: ${PrimaryTxtInvDefault};

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: ${pxToRem(48)};
    height: ${pxToRem(48)};
    background-image:
      linear-gradient(45deg, ${PrimaryBgDefault} 25%, transparent 25%,
        transparent 75%, ${PrimaryBgDefault} 75%, ${PrimaryBgDefault}),
      linear-gradient(45deg, ${PrimaryBgDefault} 25%, transparent 25%,
        transparent 75%, ${PrimaryBgDefault} 75%, ${PrimaryBgDefault});
    background-size: ${pxToRem(48)} ${pxToRem(48)};
    background-position: 0 0, ${pxToRem(24)} ${pxToRem(24)};
  }

  ${mediaQueries.lg} {
    margin: ${pxToRem(80)} 0;
    padding: ${pxToRem(64)};
    border-radius: ${pxToRem(32)};
    background-size: calc(100% - ${pxToRem(64)}) 100%, 100% calc(100% - ${pxToRem(64)});

    &::after {
      width: ${pxToRem(64)};
      height: ${pxToRem(64)};
      background-size: ${pxToRem(64)} ${pxToRem(64)};
      background-position: 0 0, ${pxToRem(32)} ${pxToRem(32)};
    }
  }

  p, h2, h3, h4 {
    color: inherit;
  }

  a:not([class]) {
    color: ${colors.snow} !important;
  }

  > *:last-child,
  ul:last-child li:last-of-type {
    margin-bottom: 0;
  }

  ul li::before {
    background: ${colors.snow};
  }

  .btn {
    position: relative;
    display: inline-flex;
    justify-content: center;
    padding: ${pxToRem(10)} ${pxToRem(18)};
    border: ${pxToRem(2)} solid ${colors.white};
    border-radius: ${pxToRem(32)};
    font-family: "Space Grotesk", sans-serif;
    font-size: ${pxToRem(16)};
    line-height: 1;
    text-align: center;
    text-decoration: none;
    font-weight: ${weights.bold};
    transition: ${transition()};
    white-space: nowrap;
    background: transparent;
    color: ${colors.white};

    &:hover,
    &:active {
      background: ${colors.white};
      border-color: ${colors.white};
      color: ${colors.black900};
    }

    &::before {
      content: '';
      position: absolute;
      top: ${pxToRem(-4)};
      right: ${pxToRem(-4)};
      bottom: ${pxToRem(-4)};
      left: ${pxToRem(-4)};
      border-radius: inherit;
      box-shadow: none;
      transition: ${transition()};
    }

    ${mediaQueries.lg} {
      padding: ${pxToRem(18)} ${pxToRem(22)};
    }

    ${mediaQueries.xl} {
      padding: ${pxToRem(17)} ${pxToRem(30)};
      border-radius: ${pxToRem(40)};
      font-size: ${pxToRem(18)};
    }

    &:focus-visible {
      outline: none;
      border-color: ${rgbaFromHex(colors.neutral400, 30)};

      &::before {
        box-shadow: 0 0 ${pxToRem(2)} ${pxToRem(2)} ${colors.yellow600};
      }
    }
  }
`;

export const blockquoteStyles = css`
  margin: ${pxToRem(56)} 0;
  border-left: ${pxToRem(8)} solid ${colors.violet600};
  padding-left: ${pxToRem(32)};

  ${mediaQueries.lg} {
    margin: ${pxToRem(80)} 0;
  }

  blockquote {
    margin: 0;
    font-family: "Space Grotesk", sans-serif;
    ${fontSizeLineHeight(16, 24)}

    ${mediaQueries.lg} {
      ${fontSizeLineHeight(20, 32)}
    }
  }

  figcaption {
    margin: ${pxToRem(24)} 0 0;
    ${fontSizeLineHeight(16, 24)}

    ${mediaQueries.lg} {
      ${fontSizeLineHeight(18, 32)}
    }
  }
`;

// Styles shared between both longText versions
const longTextStylesBase = css`
  figure.table {
    overflow-x: auto;
  }

  img {
    border-radius: ${pxToRem(16)};

    ${mediaQueries.lg} {
      border-radius: ${pxToRem(32)};
    }
  }

  pre, code {
    white-space: pre;
  }
`

// Non-responsive, condensed typography meant for the Trust Center
export const longTextStylesSm = css`
  ${longTextStylesBase}

  p {
    ${bodySm}
  }

  figure {
    margin: ${pxToRem(56)} 0;
  }

  h2 {
    ${h3Sm}
  }

  h3 {
    ${h4Sm}
  }

  h4 {
    ${h5Sm}
  }

  h5 {
    ${bodySm}
    font-weight: ${weights.bold};
  }

  ul {
    ${bulletsSm}
  }

  ol {
    ${numbersSm}
  }

  table {
    ${tableSm}
  }
`;

// Currently used for blog + case studies
export const longTextStyles = css`
  ${longTextStylesBase}

  figure {
    ${respSpacing('margin-block', 'xl')}
  }

  h2 {
    ${h3}
  }

  h3 {
    ${h4}
  }

  h4 {
    ${h5}
  }

  h5 {
    ${h6}

    ${mediaQueries.md} {
      ${fontSizeLineHeight(24, 36)}
    }

    ${mediaQueries.lg} {
      ${fontSizeLineHeight(24, 36)}
    }
  }

  .quote {
    ${blockquoteStyles}
  }

  .callout-muted {
    ${calloutMutedStyles}
  }

  .callout-strong {
    ${calloutStrongStyles}
  }

  .callout-strong--two-col {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;

    ${mediaQueries.lg} {
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: space-between;
      text-align: left;
    }

    p {
      font-family: "Space Grotesk", sans-serif;
      ${fontSizeLineHeight(18, 28)}

      ${mediaQueries.lg} {
        margin-bottom: 0;
        margin-right: ${pxToRem(40)};
        ${fontSizeLineHeight(22, 36)}
      }
    }
  }

  .callout-strong--icon {
    > :first-child {
      position: relative;
      padding-top: ${pxToRem(48)};

      ${mediaQueries.lg} {
        padding-top: 0;
        padding-left: ${pxToRem(40)};
      }

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: ${pxToRem(40)};
        background-size: ${pxToRem(40)} auto;
        background-position: top center;
        background-repeat: no-repeat;

        ${mediaQueries.lg} {
          background-size: ${pxToRem(32)} auto;
          background-position: top left;
          height: 100%;
          width: ${pxToRem(32)};
        }
      }
    }
  }

  .callout-strong--icon-leaf {
    > :first-child::before {
      background-image: url('/greener-regions/leaf-icon.svg');
    }
  }

  .disclaimer {
    font-size: ${pxToRem(13)};
    line-height: ${pxToRem(20)};

    p {
      font-size: inherit;
      line-height: inherit;
    }
  }

  video {
    max-width: 100%;
    margin-bottom: ${pxToRem(16)};
    border-radius: ${pxToRem(16)};
  }

  iframe[src*="youtube"],
  iframe[src*="vimeo"] {
    width: 100%;
    aspect-ratio: 16 / 9;
    height: auto !important;
    ${respSpacing('margin-block-end', 'm')}
  }
`;

// Used for dynamic pages
export const longTextStylesDynamic = css`
  ${longTextStylesBase}

  figure {
    ${respSpacing('margin-block', 'xl')}
    ${respSpacing('margin-inline', '0')}

    &:first-child {
      ${respSpacing('margin-block-start', '0')}
    }

    &:last-child {
      ${respSpacing('margin-block-end', '0')}
    }
  }

  .quote {
    ${blockquoteStyles}
  }

  .callout-muted {
    ${calloutMutedStyles}
  }

  .callout-strong {
    ${calloutStrongStyles}
  }
`;

// Sets the margin on elements in a long text field to the tops rather than
// the bottoms. Currently used on HeroWithForm.
// Eventually this will become the default everywhere
export const longTextMarginsTop = css`
  p {
    ${respSpacing('margin-block-start', 'xs')}
  }

  h2 {
    ${respSpacing('margin-block-start', 'xl')}
  }

  h3,
  h4 {
    ${respSpacing('margin-block-start', 'm')}
  }

  h5{
    ${respSpacing('margin-block-start', 's')}
  }

  *,
  ul:not([class]),
  ol:not([class]),
  ul:not([class]) li,
  ol:not([class]) li { // stupid specificity, grr
    ${respSpacing('margin-block-end', '0')}
  }

  *:first-child {
    ${respSpacing('margin-block-start', '0')}
  }
`
